import {useEffect, useState} from 'react';
import type {DraggableProvided, DroppableProps, DroppableProvided} from 'react-beautiful-dnd';
import {DragDropContext, Draggable, Droppable} from 'react-beautiful-dnd';

export interface DraggableItem {
  id: string;
  item: JSX.Element;
  isDragDisabled?: boolean;
}
interface Props {
  items: DraggableItem[];
  handleOnReorder?: (itemIds: string[]) => void;
  disabled?: boolean;
}

const StrictModeDroppable: React.FC<DroppableProps> = ({children, ...props}) => {
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    const animation = requestAnimationFrame(() => setEnabled(true));

    return () => {
      cancelAnimationFrame(animation);
      setEnabled(false);
    };
  }, []);

  if (!enabled) {
    return null;
  }

  return <Droppable {...props}>{children}</Droppable>;
};

const DraggableList: React.FC<Props> = ({items, handleOnReorder, disabled}) => {
  const reorder = (
    list: DraggableItem[],
    startIndex: number,
    endIndex: number
  ): DraggableItem[] => {
    const results = [...list];
    const [removed] = results.splice(startIndex, 1) as [DraggableItem, ...DraggableItem[]];
    results.splice(endIndex, 0, removed);
    if (handleOnReorder) {
      handleOnReorder(results.map(item => item.id));
    }
    return results;
  };

  return (
    <DragDropContext
      onDragEnd={({source, destination}) => {
        if (!destination) {
          return;
        }
        reorder(items, source.index, destination.index);
      }}
    >
      <StrictModeDroppable droppableId="droppable" isDropDisabled={disabled}>
        {(provided: DroppableProvided) => (
          <div {...provided.droppableProps} ref={provided.innerRef}>
            {items.map((item: DraggableItem, index: number) => {
              return (
                <Draggable
                  key={item.id}
                  draggableId={item.id}
                  index={index}
                  isDragDisabled={item.isDragDisabled === true}
                >
                  {(provided: DraggableProvided) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                      style={provided.draggableProps.style}
                    >
                      {item.item}
                    </div>
                  )}
                </Draggable>
              );
            })}
            {provided.placeholder}
          </div>
        )}
      </StrictModeDroppable>
    </DragDropContext>
  );
};

export default DraggableList;
